import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "@heroicons/react/outline";

/**
 * A fully section-driven knowledge base component with collapsible cards.
 *
 * Props:
 *   keys.title       - translation key for the header title
 *   keys.subtitle    - translation key for the header subtitle
 *   keys.sections[]  - ordered array of section objects.
 *                      Sections with 'intro' type are rendered in the main header card.
 *                      Other sections are rendered as individual collapsible cards.
 */
const KnowledgeBaseSection = ({ keys }) => {
  const { t } = useTranslation();

  // State to track expanded sections by index
  // Initialize with the first non-intro section expanded (optional, or start all closed)
  const [expandedSections, setExpandedSections] = useState({ 0: true });

  const toggleSection = (index) => {
    setExpandedSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderDescParts = (parts) =>
    parts.map((part, i) => {
      const space = i > 0 ? " " : "";
      if (part.bold) {
        return (
          <React.Fragment key={i}>
            {space}
            <strong>{t(part.text)}</strong>
          </React.Fragment>
        );
      }
      if (part.connector) {
        return <React.Fragment key={i}>{` ${t(part.text)} `}</React.Fragment>;
      }
      return (
        <React.Fragment key={i}>
          {space}
          {t(part.text)}
        </React.Fragment>
      );
    });

  const gridColsClass = (n) => {
    const map = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" };
    return map[n] || "grid-cols-2";
  };

  const renderSectionContent = (section, index) => {
    switch (section.type) {
      case "intro":
        return (
          <p key={index} className="text-sm text-slate-700 leading-relaxed">
            {renderDescParts(section.parts)}
          </p>
        );

      case "labeled-list":
        return (
          <div
            key={index}
            className="space-y-3 text-sm text-slate-700 leading-relaxed"
          >
            {section.items.map((item, i) => (
              <p key={i}>
                <strong>{t(item.labelKey)}</strong> {t(item.descKey)}
              </p>
            ))}
          </div>
        );

      case "list":
        return (
          <div
            key={index}
            className="space-y-3 text-sm text-slate-700 leading-relaxed"
          >
            {section.description && <p>{t(section.description)}</p>}
            <ul className="list-disc list-inside space-y-1 text-slate-600 ml-1">
              {section.items.map((itemKey, i) => (
                <li key={i}>{t(itemKey)}</li>
              ))}
            </ul>
          </div>
        );

      case "text":
        return (
          <div
            key={index}
            className="space-y-3 text-sm text-slate-700 leading-relaxed"
          >
            {section.description && <p>{t(section.description)}</p>}
            {section.descriptions &&
              section.descriptions.map((desc, i) => <p key={i}>{t(desc)}</p>)}
          </div>
        );

      case "subsections":
        return (
          <div
            key={index}
            className="space-y-4 text-sm text-slate-700 leading-relaxed"
          >
            {section.descParts && <p>{renderDescParts(section.descParts)}</p>}
            {section.description && <p>{t(section.description)}</p>}

            {section.subsections.map((sub, i) => (
              <div key={i} className="space-y-2">
                <h4 className="text-sm font-medium text-slate-800">
                  {t(sub.title)}
                </h4>
                {sub.description && <p>{t(sub.description)}</p>}
                <div className="rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
                  {sub.items.map((item, j) => (
                    <div
                      key={j}
                      className={
                        sub.layout === "label-value"
                          ? "flex justify-between px-4 py-3"
                          : "px-4 py-3"
                      }
                    >
                      {sub.layout === "label-value" ? (
                        <>
                          <span className="text-slate-600">
                            {t(item.labelKey)}
                          </span>
                          <span className="font-medium text-slate-900">
                            {item.value}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium text-slate-900">
                            {item.labelKey
                              ? t(item.labelKey, item.interpolation || {})
                              : ""}
                          </span>{" "}
                          <span className="text-slate-600">
                            {t(item.descKey)}
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case "methods":
        return (
          <div
            key={index}
            className="space-y-4 text-sm text-slate-700 leading-relaxed"
          >
            {section.description && <p>{t(section.description)}</p>}

            {section.methods.map((method, i) => (
              <div key={i} className="space-y-2">
                <h4 className="text-sm font-medium text-slate-800">
                  {t(method.title)}
                </h4>
                {method.steps ? (
                  <div className="space-y-2">
                    {method.steps.map((step, j) => (
                      <React.Fragment key={j}>
                        <p className="text-slate-600">
                          <strong>{t(step.labelKey)}</strong>
                        </p>
                        <div className="bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl font-mono text-xs text-slate-700">
                          {t(step.formulaKey)}
                        </div>
                        {step.noteKey && (
                          <p className="text-xs text-slate-500">
                            {t(step.noteKey)}
                          </p>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                ) : method.rows ? (
                  <div className="rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
                    {method.rows.map((row, j) => (
                      <div key={j} className="px-4 py-3 space-y-1">
                        <p className="text-slate-600">{t(row.labelKey)}</p>
                        <p className="font-mono text-xs text-slate-700">
                          {t(row.formulaKey)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : method.formulaKey ? (
                  <>
                    <div className="bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl font-mono text-xs text-slate-700">
                      {t(method.formulaKey)}
                    </div>
                    {method.noteKey && (
                      <p className="text-xs text-slate-500">
                        {t(method.noteKey)}
                      </p>
                    )}
                  </>
                ) : null}
              </div>
            ))}
          </div>
        );

      case "table":
        return (
          <div
            key={index}
            className="space-y-3 text-sm text-slate-700 leading-relaxed"
          >
            {section.description && <p>{t(section.description)}</p>}
            <div className="rounded-xl border border-slate-200/60 overflow-hidden">
              <div
                className={`grid ${gridColsClass(section.headers.length)} bg-slate-100 px-4 py-2.5`}
              >
                {section.headers.map((header, i) => (
                  <span
                    key={i}
                    className={`text-xs font-medium text-slate-500 uppercase tracking-wider ${i > 0 ? "text-right" : ""}`}
                  >
                    {t(header)}
                  </span>
                ))}
              </div>
              {section.rows.map((row, i) => (
                <div
                  key={i}
                  className={`grid ${gridColsClass(section.headers.length)} px-4 py-2.5 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                >
                  {row.cells.map((cell, j) => (
                    <span
                      key={j}
                      className={
                        j === 0
                          ? "text-slate-700"
                          : "text-slate-900 font-medium text-right"
                      }
                    >
                      {cell.key ? t(cell.key) : cell.value}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );

      case "label-value":
        return (
          <div
            key={index}
            className="space-y-3 text-sm text-slate-700 leading-relaxed"
          >
            {section.description && <p>{t(section.description)}</p>}
            <div className="rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
              {section.items.map((item, i) => (
                <div key={i} className="flex justify-between px-4 py-2.5">
                  <span className="text-slate-600">{t(item.labelKey)}</span>
                  <span className="font-medium text-slate-900">
                    {t(item.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case "key-value":
        return (
          <div
            key={index}
            className="space-y-3 text-sm text-slate-700 leading-relaxed"
          >
            {section.description && <p>{t(section.description)}</p>}

            {section.items && (
              <div className="rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
                {section.items.map((item, i) => (
                  <div key={i} className="flex justify-between px-4 py-2.5">
                    <span className="text-slate-600">{t(item.labelKey)}</span>
                    <span className="font-medium text-slate-900 tabular-nums">
                      {item.amount}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {section.statusTitle && (
              <>
                <p className="font-medium text-slate-800 pt-1">
                  {t(section.statusTitle)}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {section.statuses.map((item, i) => (
                    <div
                      key={i}
                      className="bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5 text-center"
                    >
                      <span className="block text-xs font-semibold text-slate-900">
                        {item.status}
                      </span>
                      <span className="block text-xs text-slate-500 tabular-nums mt-0.5">
                        {item.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      case "rate-info":
        return (
          <div
            key={index}
            className="space-y-3 text-sm text-slate-700 leading-relaxed"
          >
            {section.description && <p>{t(section.description)}</p>}
            <div className="rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
              {section.rates.map((rate, i) => (
                <div
                  key={i}
                  className={
                    rate.descKey
                      ? "px-4 py-3"
                      : "flex justify-between px-4 py-3"
                  }
                >
                  {rate.descKey ? (
                    <>
                      <span className="font-medium text-slate-900">
                        {t(rate.labelKey)}
                      </span>{" "}
                      <span className="text-slate-600">{t(rate.descKey)}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-slate-600">{t(rate.labelKey)}</span>
                      <span className="font-medium text-slate-900">
                        {t(rate.valueKey)}
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Group intro sections (to be rendered in the header card) and content sections (collapsible cards)
  const introSections = keys.sections.filter((s) => s.type === "intro");
  const contentSections = keys.sections.filter((s) => s.type !== "intro");

  return (
    <div className="space-y-4">
      {/* Header / Intro Card */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900 tracking-tight">
            {t(keys.title)}
          </h2>
          <p className="text-sm text-slate-500 mt-1">{t(keys.subtitle)}</p>
        </div>
        {introSections.length > 0 && (
          <div className="px-6 py-5 space-y-4">
            {introSections.map((section, i) =>
              renderSectionContent(section, i),
            )}
          </div>
        )}
      </div>

      {/* Content Sections as Collapsible Cards */}
      {contentSections.map((section, i) => {
        const isExpanded = expandedSections[i];

        return (
          <div
            key={i}
            className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden transition-all duration-200"
          >
            <button
              onClick={() => toggleSection(i)}
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors duration-150"
            >
              <h3 className="text-sm font-semibold text-slate-900">
                {t(section.title)}
              </h3>
              <ChevronDownIcon
                className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isExpanded ? "transform rotate-180" : ""}`}
              />
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 pt-2 border-t border-slate-100 animate-fadeIn">
                {renderSectionContent(section, i)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default KnowledgeBaseSection;
